/**
 * useComboboxEscape — Escape при открытом выпадающем списке combobox'а закрывает ТОЛЬКО список,
 * не задевая модальный диалог, внутри которого поле может быть смонтировано.
 *
 * Почему недостаточно ветки `case 'Escape'` в React-обработчике `onKeyDown`: модальный Radix
 * `Dialog` (`DismissableLayer`) вешает свой обработчик как
 * `ownerDocument.addEventListener('keydown', handler, { capture: true })`, а React 18 слушает
 * события в фазе ВСПЛЫТИЯ на корне приложения. Захват на `document` отрабатывает раньше, поэтому
 * к моменту нашего `preventDefault()` Radix уже увидел `defaultPrevented === false` и вызвал
 * `onDismiss()` — диалог закрыт. Пользователь, нажавший Escape просто чтобы убрать список,
 * терял всю форму с несохранёнными данными (`AddWorkplaceForm` внутри `AddToStructureModal`
 * на /ot и внутри `WorkplacesCard` на «Сотрудники»/team-«Структура»).
 *
 * Поток события идёт `window` → `document` → … → target, поэтому захват на `window` срабатывает
 * ПЕРЕД захватом на `document`: `stopPropagation()` здесь гасит обработчик Radix и не трогает
 * остальные слои. Слушатель живёт только пока список открыт и реагирует лишь на события внутри
 * своего поля, поэтому второй Escape (список уже закрыт) закрывает диалог, как и раньше.
 *
 * Клика вне списка это не касается: Radix ловит его через `onPointerDownCapture` в React-дереве,
 * а оно проходит сквозь порталы — там наш портал уже «внутри» диалога.
 */
import { useEffect, useRef, type RefObject } from 'react'

export function useComboboxEscape(
  /** Список раскрыт — только в этом состоянии перехватываем Escape. */
  open: boolean,
  /** Обёртка поля: Escape перехватывается лишь для событий внутри неё. */
  anchorRef: RefObject<HTMLElement>,
  /** Закрытие списка (без изменения значения поля). */
  close: () => void,
): void {
  // Колбэк держим в ref, чтобы не перевешивать слушатель на каждый рендер.
  const closeRef = useRef(close)
  useEffect(() => {
    closeRef.current = close
  })

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const target = e.target as Node | null
      if (!target || !anchorRef.current?.contains(target)) return
      e.stopPropagation()
      e.preventDefault()
      closeRef.current()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [open, anchorRef])
}
